import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey } from '../../api/api-error';
import { ensureOk, type Profile } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';

type Values = { newEmail: string; password: string; code: string };

export function EmailSection({ profile }: { profile: Profile }) {
  const { t } = useI18n();
  // Email — канал восстановления доступа: при 2FA сервер требует ещё и код (ADR-0013).
  const schema = useMemo(
    () =>
      z.object({
        newEmail: z.string().trim().min(1, 'validation.required').pipe(z.email('validation.email')),
        password: z.string().min(1, 'validation.required'),
        code: profile.twoFactorEnabled ? z.string().trim().min(6, 'validation.code') : z.string(),
      }),
    [profile.twoFactorEnabled],
  );
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { newEmail: '', password: '', code: '' },
  });
  const change = useMutation({
    mutationFn: async ({ code, ...body }: Values) =>
      ensureOk(
        await api.POST('/api/v1/me/email', {
          body: profile.twoFactorEnabled ? { ...body, code } : body,
        }),
      ),
    onSuccess: () => form.reset(),
  });

  return (
    <Card title={t('profile.email.title')}>
      <p className="text-sm">
        {profile.email}{' '}
        <span className="text-muted-foreground">
          ({profile.emailVerified ? t('profile.email.verified') : t('profile.email.unverified')})
        </span>
      </p>
      {profile.hasPassword ? (
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit((values) => change.mutate(values))}
          noValidate
        >
          <TextField
            control={form.control}
            name="newEmail"
            type="email"
            label={t('profile.email.new')}
            autoComplete="email"
          />
          <TextField
            control={form.control}
            name="password"
            type="password"
            label={t('auth.field.currentPassword')}
            autoComplete="current-password"
          />
          {profile.twoFactorEnabled && (
            <TextField
              control={form.control}
              name="code"
              label={t('auth.field.code')}
              autoComplete="one-time-code"
            />
          )}
          {change.isSuccess && <Notice tone="success">{t('profile.email.sent')}</Notice>}
          {change.isError && <Notice tone="danger">{t(errorMessageKey(change.error))}</Notice>}
          <Button type="submit" variant="secondary" disabled={change.isPending}>
            {t('profile.email.submit')}
          </Button>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground">{t('profile.passwordRequired')}</p>
      )}
    </Card>
  );
}
